import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import {
  Loader2,
  Save,
  Copy,
  ExternalLink,
  CreditCard,
  CheckCircle,
  AlertCircle,
  DollarSign,
} from "lucide-react";

const DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const SettingsPage = () => {
  const { api, business, refreshBusiness, user, subscription } = useAuth();
  const [loading, setLoading] = useState(false);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [depositLoading, setDepositLoading] = useState(false);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [depositPercentage, setDepositPercentage] = useState(0);
  const [subData, setSubData] = useState(null);
  const [subLoading, setSubLoading] = useState(false);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    description: "",
    timezone: "",
    business_hours: [],
  });

  useEffect(() => {
    if (business) {
      setFormData({
        name: business.name || "",
        email: business.email || "",
        phone: business.phone || "",
        address: business.address || "",
        description: business.description || "",
        timezone: business.timezone || "UTC",
        business_hours:
          business.business_hours ||
          DAYS.map((_, i) => ({
            day: i,
            open_time: "09:00",
            close_time: "18:00",
            is_closed: i >= 5,
          })),
      });
      fetchStripeStatus();
      fetchSubscription();
    }
  }, [business]);

  const fetchSubscription = async () => {
    try {
      const response = await api.get("/subscription/current");
      setSubData(response.data);
    } catch (error) {
      console.error("Failed to fetch subscription:", error);
    }
  };

  const fetchStripeStatus = async () => {
    try {
      const response = await api.get("/stripe/connect/status");
      setStripeStatus(response.data);
      setDepositPercentage(response.data.deposit_percentage || 0);
    } catch (error) {
      console.error("Failed to fetch Stripe status:", error);
    }
  };

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const updateHours = (dayIndex, field, value) => {
    setFormData((prev) => ({
      ...prev,
      business_hours: prev.business_hours.map((h, i) =>
        i === dayIndex ? { ...h, [field]: value } : h,
      ),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error("Business name is required");
      return;
    }

    setLoading(true);
    try {
      await api.put(`/businesses/${business.id}`, formData);
      await refreshBusiness();
      toast.success("Settings saved successfully");
    } catch (error) {
      console.error("Failed to save settings:", error);
      toast.error(error.response?.data?.detail || "Failed to save settings");
    } finally {
      setLoading(false);
    }
  };

  const handleConnectStripe = async () => {
    setStripeLoading(true);
    try {
      const response = await api.post("/stripe/connect/create-link", {
        return_url: `${window.location.origin}/dashboard/settings?stripe_return=true`,
        refresh_url: `${window.location.origin}/dashboard/settings?stripe_refresh=true`,
      });
      
      // Redirect to Stripe onboarding
      window.location.href = response.data.url;
    } catch (error) {
      console.error("Failed to create Stripe link:", error);
      toast.error(error.response?.data?.detail || "Failed to connect Stripe");
      setStripeLoading(false);
    }
  };

  const handleUpdateDeposit = async () => {
    setDepositLoading(true);
    try {
      await api.put(`/stripe/deposit-settings?deposit_percentage=${depositPercentage}`);
      toast.success("Deposit settings updated");
      fetchStripeStatus();
    } catch (error) {
      console.error("Failed to update deposit:", error);
      toast.error(error.response?.data?.detail || "Failed to update deposit settings");
    } finally {
      setDepositLoading(false);
    }
  };

  const copyBookingLink = () => {
    const link = `${window.location.origin}/book/${business?.slug}`;
    navigator.clipboard.writeText(link);
    toast.success("Booking link copied!");
  };

  // Check for Stripe return or subscription return
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("stripe_return") === "true") {
      toast.success("Stripe setup progress saved!");
      fetchStripeStatus();
      window.history.replaceState({}, "", "/dashboard/settings");
    } else if (params.get("stripe_refresh") === "true") {
      toast.info("Please complete Stripe setup");
      window.history.replaceState({}, "", "/dashboard/settings");
    } else if (params.get("subscription") === "success") {
      const sessionId = params.get("session_id");
      if (sessionId) {
        pollSubscriptionStatus(sessionId);
      }
      window.history.replaceState({}, "", "/dashboard/settings");
    } else if (params.get("subscription") === "cancelled") {
      toast.info("Subscription checkout was cancelled");
      window.history.replaceState({}, "", "/dashboard/settings");
    }
  }, []);

  const pollSubscriptionStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) {
      toast.error("Could not verify payment. Please refresh the page.");
      return;
    }
    setSubLoading(true);
    try {
      const response = await api.get(`/subscription/checkout-status/${sessionId}`);
      if (response.data.payment_status === "paid") {
        toast.success("Subscription activated! Welcome aboard.");
        fetchSubscription();
        refreshBusiness();
        setSubLoading(false);
      } else if (response.data.status === "expired") {
        toast.error("Payment session expired. Please try again.");
        setSubLoading(false);
      } else {
        setTimeout(() => pollSubscriptionStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      console.error("Failed to check subscription status:", error);
      setSubLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm("Are you sure you want to cancel your subscription? You'll retain access until the end of the billing period.")) return;
    setCancellingSubscription(true);
    try {
      const response = await api.post("/subscription/cancel");
      toast.success(response.data.message);
      fetchSubscription();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel");
    } finally {
      setCancellingSubscription(false);
    }
  };

  const bookingUrl = `${window.location.origin}/book/${business?.slug}`;

  return (
    <DashboardLayout>
      <div className="max-w-3xl space-y-8" data-testid="settings-page">
        {/* Header */}
        <div>
          <h1 className="font-heading text-3xl font-extrabold">Settings</h1>
          <p className="text-muted-foreground">
            Manage your business profile and preferences.
          </p>
        </div>

        {/* Booking Link */}
        <div className="card-base">
          <h2 className="font-heading font-bold text-lg mb-4">
            Public Booking Link
          </h2>
          <p className="text-sm text-muted-foreground mb-4">
            Share this link with your customers so they can book appointments
            online.
          </p>

          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              value={bookingUrl}
              readOnly
              className="flex-1 bg-secondary"
            />
            <Button
              variant="outline"
              onClick={copyBookingLink}
              className="w-full sm:w-auto"
            >
              <Copy className="w-4 h-4 mr-2" />
              Copy
            </Button>
            <a href={bookingUrl} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="w-full sm:w-auto">
                <ExternalLink className="w-4 h-4" />
              </Button>
            </a>
          </div>
        </div>

        {/* Stripe Connect & Payment Settings */}
        <div className="card-base" data-testid="stripe-settings">
          <h2 className="font-heading font-bold text-lg mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            Payment Settings
          </h2>
          
          {/* Stripe Connect Status */}
          <div className="space-y-4">
            <div className="p-4 rounded-lg border border-border bg-secondary/30">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-medium mb-1">Stripe Connect</h3>
                  <p className="text-sm text-muted-foreground">
                    Connect your Stripe account to receive payments directly from customers.
                  </p>
                </div>
                {stripeStatus?.onboarding_complete ? (
                  <div className="flex items-center gap-2 text-green-600 bg-green-50 px-3 py-1.5 rounded-full text-sm font-medium">
                    <CheckCircle className="w-4 h-4" />
                    Connected
                  </div>
                ) : stripeStatus?.connected ? (
                  <div className="flex items-center gap-2 text-yellow-600 bg-yellow-50 px-3 py-1.5 rounded-full text-sm font-medium">
                    <AlertCircle className="w-4 h-4" />
                    Setup Incomplete
                  </div>
                ) : null}
              </div>
              
              {!stripeStatus?.onboarding_complete && (
                <Button
                  onClick={handleConnectStripe}
                  disabled={stripeLoading}
                  className="mt-4 w-full sm:w-auto"
                  data-testid="connect-stripe-btn"
                >
                  {stripeLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Connecting...
                    </>
                  ) : stripeStatus?.connected ? (
                    <>
                      <CreditCard className="w-4 h-4 mr-2" />
                      Complete Stripe Setup
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4 mr-2" />
                      Connect Stripe Account
                    </>
                  )}
                </Button>
              )}
            </div>

            {/* Deposit Settings - Only show when Stripe is connected */}
            {stripeStatus?.onboarding_complete && (
              <div className="p-4 rounded-lg border border-border">
                <div className="flex items-center gap-2 mb-3">
                  <DollarSign className="w-5 h-5 text-primary" />
                  <h3 className="font-medium">Booking Deposit</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  Require customers to pay a deposit when booking. Set to 0% for no deposit required.
                </p>
                
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <Slider
                        value={[depositPercentage]}
                        onValueChange={(value) => setDepositPercentage(value[0])}
                        max={100}
                        step={5}
                        className="w-full"
                        data-testid="deposit-slider"
                      />
                    </div>
                    <div className="w-20 text-center">
                      <span className="text-2xl font-bold text-primary">{depositPercentage}%</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">
                      {depositPercentage === 0 ? (
                        "No deposit required - customers pay in store"
                      ) : depositPercentage === 100 ? (
                        "Full payment required at booking"
                      ) : (
                        `${depositPercentage}% deposit at booking, ${100 - depositPercentage}% due at appointment`
                      )}
                    </span>
                  </div>

                  {/* Example calculation */}
                  {depositPercentage > 0 && (
                    <div className="p-3 bg-secondary/50 rounded-lg text-sm">
                      <p className="font-medium mb-1">Example: $100 service</p>
                      <div className="flex justify-between text-muted-foreground">
                        <span>Deposit (online):</span>
                        <span className="font-medium text-foreground">${depositPercentage}</span>
                      </div>
                      <div className="flex justify-between text-muted-foreground">
                        <span>Due at appointment:</span>
                        <span className="font-medium text-foreground">${100 - depositPercentage}</span>
                      </div>
                    </div>
                  )}
                  
                  <Button
                    onClick={handleUpdateDeposit}
                    disabled={depositLoading || depositPercentage === stripeStatus?.deposit_percentage}
                    variant="outline"
                    className="w-full sm:w-auto"
                    data-testid="save-deposit-btn"
                  >
                    {depositLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Deposit Settings
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Info when Stripe not connected */}
            {!stripeStatus?.connected && (
              <div className="p-3 bg-blue-50 text-blue-700 rounded-lg text-sm">
                <p>
                  <strong>Why connect Stripe?</strong> Accept deposits and payments directly from customers. 
                  Funds go straight to your bank account. No card data is stored on SlotBook.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Business Profile */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="card-base">
            <h2 className="font-heading font-bold text-lg mb-4">
              Business Profile
            </h2>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Business Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => updateField("name", e.target.value)}
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Contact Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => updateField("email", e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone Number</Label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => updateField("phone", e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Address</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => updateField("address", e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  rows={3}
                  value={formData.description}
                  onChange={(e) => updateField("description", e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Business Hours */}
          <div className="card-base min-w-0 !p-4 sm:!p-6">
            <h2 className="font-heading font-bold text-lg mb-4">
              Business Hours
            </h2>

            <div className="space-y-3">
              {DAYS.map((day, i) => (
                <div
                  key={day}
                  className="flex min-w-0 flex-col gap-3 rounded-lg border border-border p-3 sm:flex-row sm:items-center sm:gap-4"
                >
                  <div className="sm:w-28">
                    <span className="font-medium">{day}</span>
                  </div>

                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={!formData.business_hours[i]?.is_closed}
                      onChange={(e) =>
                        updateHours(i, "is_closed", !e.target.checked)
                      }
                    />
                    <span className="text-sm text-muted-foreground">Open</span>
                  </label>

                  {!formData.business_hours[i]?.is_closed && (
                    <div className="flex min-w-0 w-full flex-col gap-2 sm:ml-auto sm:w-auto sm:flex-row">
                      <Input
                        type="time"
                        value={formData.business_hours[i]?.open_time}
                        onChange={(e) =>
                          updateHours(i, "open_time", e.target.value)
                        }
                        className="min-w-0 w-full sm:w-32"
                      />
                      <Input
                        type="time"
                        value={formData.business_hours[i]?.close_time}
                        onChange={(e) =>
                          updateHours(i, "close_time", e.target.value)
                        }
                        className="min-w-0 w-full sm:w-32"
                      />
                    </div>
                  )}

                  {formData.business_hours[i]?.is_closed && (
                    <span className="sm:ml-auto text-sm text-muted-foreground">
                      Closed
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Subscription Plan */}
          <div className="card-base" data-testid="subscription-settings">
            <h2 className="font-heading font-bold text-lg mb-4 flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Subscription Plan
            </h2>

            {subLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400 mr-2" />
                <span className="text-sm text-gray-500">Verifying payment...</span>
              </div>
            ) : subData ? (
              <div className="space-y-4">
                {/* Current Status */}
                <div className="flex items-center justify-between p-4 rounded-xl border border-gray-200 bg-gray-50">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${
                        subData.status === "active"
                          ? "bg-emerald-100 text-emerald-700"
                          : subData.status === "trial"
                          ? "bg-amber-100 text-amber-700"
                          : "bg-red-100 text-red-700"
                      }`}>
                        {subData.status === "active" ? (
                          <><CheckCircle className="w-3 h-3" /> Active</>
                        ) : subData.status === "trial" ? (
                          <><AlertCircle className="w-3 h-3" /> Trial</>
                        ) : (
                          <><AlertCircle className="w-3 h-3" /> Expired</>
                        )}
                      </span>
                      {subData.plan && (
                        <span className="text-sm font-semibold text-gray-900">
                          {subData.plan.name} Plan
                        </span>
                      )}
                    </div>
                    {subData.plan && (
                      <p className="text-sm text-gray-500 mt-1">
                        ${subData.plan.price}/{subData.plan.interval}
                      </p>
                    )}
                    {subData.trial && (
                      <p className="text-sm text-gray-500 mt-1">
                        {subData.trial.days_remaining} days remaining in trial
                      </p>
                    )}
                    {subData.current_period_end && subData.status === "active" && (
                      <p className="text-xs text-gray-400 mt-1">
                        Next billing: {new Date(subData.current_period_end).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}
                      </p>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-2">
                  {subData.status !== "active" && (
                    <Button
                      onClick={() => window.location.href = "/dashboard/pricing"}
                      className="bg-indigo-500 hover:bg-indigo-600 text-white"
                      data-testid="upgrade-plan-btn"
                    >
                      <DollarSign className="w-4 h-4 mr-1" />
                      {subData.status === "trial" ? "Upgrade Now" : "Reactivate"}
                    </Button>
                  )}
                  {subData.status === "active" && (
                    <>
                      <Button
                        variant="outline"
                        onClick={() => window.location.href = "/dashboard/pricing"}
                        data-testid="change-plan-btn"
                      >
                        Change Plan
                      </Button>
                      <Button
                        variant="ghost"
                        className="text-gray-400 hover:text-red-500"
                        onClick={handleCancelSubscription}
                        disabled={cancellingSubscription}
                        data-testid="cancel-sub-btn"
                      >
                        {cancellingSubscription ? "Cancelling..." : "Cancel Subscription"}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-6 text-gray-400 text-sm">
                <p>Unable to load subscription info.</p>
              </div>
            )}
          </div>

          {/* Account Info */}
          <div className="card-base">
            <h2 className="font-heading font-bold text-lg mb-4">
              Account Information
            </h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Account Email</span>
                <span className="font-medium">{user?.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Role</span>
                <span className="font-medium capitalize">{user?.role}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Business Slug</span>
                <span className="font-medium">{business?.slug}</span>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={loading}
              className="w-full sm:w-auto"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
};

export default SettingsPage;
